import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Building2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const OrgBackfillSection = () => {
  const [rows, setRows] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [selected, setSelected] = useState([]);
  const [orgId, setOrgId] = useState('');
  const [assigning, setAssigning] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, o] = await Promise.all([
        axios.get(`${API_URL}/api/admin/estimates-missing-org`, { withCredentials: true }),
        axios.get(`${API_URL}/api/organizations`, { params: { limit: 500 }, withCredentials: true }),
      ]);
      setRows(r.data?.estimates || []);
      setOrgs(o.data?.organizations || o.data || []);
    } catch {
      toast.error('Could not load org backfill data');
      setRows([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const assign = async () => {
    if (!orgId || selected.length === 0) return;
    setAssigning(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/assign-estimates-org`,
        { estimate_ids: selected, org_id: orgId }, { withCredentials: true });
      toast.success(`${res.data?.assigned} estimate(s) assigned to ${res.data?.org_name}`);
      setSelected([]);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Assignment failed');
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-org-backfill">
      <div>
        <h2 className="text-base font-semibold text-[#0F2040] flex items-center gap-2">
          <Building2 className="w-4 h-4 text-[#4ABFCF]" />Estimate Org Backfill
        </h2>
        <p className="text-xs text-slate-500 mt-1">
          Legacy estimates with no organisation id are invisible to view-only org users.
          Select the estimates below, pick the organisation, and assign in one click.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Select value={orgId} onValueChange={setOrgId}>
          <SelectTrigger className="w-64 h-8 text-xs" data-testid="backfill-org-select"><SelectValue placeholder="Choose organisation…" /></SelectTrigger>
          <SelectContent>
            {orgs.map(o => <SelectItem key={o.org_id || o.id} value={o.org_id || o.id}>{o.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" onClick={assign} disabled={assigning || !orgId || selected.length === 0}
          className="bg-[#0F2040] hover:bg-[#1A3055] text-white h-8 text-xs" data-testid="backfill-assign-btn">
          {assigning ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
          Assign {selected.length > 0 ? `${selected.length} estimate(s)` : ''}
        </Button>
        {Array.isArray(rows) && rows.length > 0 && (
          <button className="text-xs text-[#4ABFCF] underline underline-offset-2"
            onClick={() => setSelected(selected.length === rows.length ? [] : rows.map(r => r.id))}
            data-testid="backfill-select-all">
            {selected.length === rows.length ? 'Clear all' : 'Select all'}
          </button>
        )}
      </div>
      {rows === null ? (
        <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#4ABFCF]" /></div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-500 py-6" data-testid="backfill-empty">Every estimate has an organisation id — nothing to fix. 🎉</p>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-[480px] overflow-y-auto">
          {rows.map(r => (
            <label key={r.id} className="flex items-center gap-3 px-3 py-2 text-xs cursor-pointer hover:bg-slate-50" data-testid={`backfill-row-${r.id}`}>
              <input type="checkbox" className="accent-[#0F2040]" checked={selected.includes(r.id)} onChange={() => toggle(r.id)} />
              <span className="font-medium text-[#0F2040] truncate flex-1">{r.estimate_name || '(untitled)'}</span>
              {r.organization_name && <span className="text-slate-500 shrink-0">name: {r.organization_name}</span>}
              {r.archived && <span className="text-[9px] px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded shrink-0">Archived</span>}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};
